import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector, useDispatch } from 'react-redux';
import { getUser } from '../slices/auth';

const TIME_PATTERN_EXAMPLES = [
  { pattern: '{{RelativeMonth|0}}',         example: 'Mar',     desc: 'current month' },
  { pattern: '{{RelativeMonth|-1}}',        example: 'Feb',     desc: 'last month' },
  { pattern: '{{RelativeMonth|-2}}',        example: 'Jan',     desc: '2 months ago' },
  { pattern: '{{RelativeYear|0}}',          example: 'FY2026',  desc: 'current fiscal year' },
  { pattern: '{{RelativeYear|-1}}',         example: 'FY2025',  desc: 'last fiscal year' },
  { pattern: '{{RelativeMonth|-1|%Y-%m}}',  example: '2025-02', desc: 'YYYY-MM format' },
  { pattern: '{{RelativeMonth|-1|%m}}',     example: '02',      desc: 'month number' },
  { pattern: '{{RelativeYear|-1|%Y}}',      example: '2025',    desc: '4-digit year' },
];

function PatternReference() {
  const [open, setOpen] = React.useState(false);
  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        startIcon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={() => setOpen((v) => !v)}
        sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#64748b', p: 0, '&:hover': { background: 'none', color: '#3b82f6' } }}
      >
        {open ? 'Hide' : 'Show'} available time patterns
      </Button>
      <Collapse in={open}>
        <Box sx={{ mt: 1, p: 1.5, borderRadius: '6px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          {TIME_PATTERN_EXAMPLES.map(({ pattern, example, desc }) => (
            <Box key={pattern} sx={{ display: 'flex', gap: 1.5, mb: 0.5, alignItems: 'baseline' }}>
              <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#0369a1', whiteSpace: 'nowrap' }}>
                {pattern}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>→</Typography>
              <Typography variant="caption" sx={{ color: '#475569' }}>
                <strong>{example}</strong> ({desc})
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function ObjectPropertyModal({
  open,
  onClose,
  elementType = '',
  dataSource = '',
  intent = '',
  query = '',
  description = '',
  queryPrompt = '',
  llmInstruction = '',
  timeParameters = [],
  onSave,
}) {
  const dispatch = useDispatch();
  const dbs = useSelector((state) => state.auth.user?.db);

  const [elementDS, setElementDS] = React.useState(dataSource);
  const [queryMode, setQueryMode] = React.useState('prompt');
  const [elementIntent, setElementIntent] = React.useState(intent);
  const [elementQuery, setElementQuery] = React.useState(query);

  React.useEffect(() => {
    if (open && !dbs) dispatch(getUser());
  }, [open, dbs, dispatch]);

  React.useEffect(() => {
    if (open) {
      setElementDS(dataSource);
      setElementIntent(intent || description);
      setElementQuery(query);
      setQueryMode(query ? 'sql' : 'prompt');
    }
  }, [open, dataSource, intent, query, description]);

  const handleSave = () => {
    if (typeof onSave === 'function') {
      onSave({
        data_source: elementDS,
        intent: queryMode === 'prompt' ? elementIntent : '',
        query: queryMode === 'sql' ? elementQuery : '',
        query_prompt: queryPrompt,
        llm_instruction: llmInstruction,
        time_parameters: timeParameters,
      });
    }
    onClose();
  };

  const label = elementType
    ? elementType.charAt(0).toUpperCase() + elementType.slice(1)
    : 'Element';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TuneIcon sx={{ fontSize: 20, color: '#64748b' }} />
        Object Properties
        <Chip
          label={label}
          size="small"
          sx={{ ml: 0.5, height: 20, fontSize: '0.7rem', backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
        />
      </DialogTitle>
      <DialogContent>

        <Box sx={{ mb: 2, mt: 0.5, p: 1.5, borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.5 }}>
            Overrides the slide-level settings for this element only. Leave fields empty to inherit from the slide.
          </Typography>
        </Box>

        {description && !intent && (
          <Box
            sx={{ mb: 1.5, p: 1.5, borderRadius: '8px', backgroundColor: '#f0f7ff', border: '1px solid #bfdbfe', display: 'flex', gap: 1, alignItems: 'flex-start' }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 16, color: '#3b82f6', mt: '2px', flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                AI Suggestion
              </Typography>
              <Typography variant="body2" sx={{ color: '#1e3a5f', lineHeight: 1.5, mt: 0.25 }}>
                {description}
              </Typography>
            </Box>
          </Box>
        )}

        <FormControl margin="dense" size="small" fullWidth variant="outlined">
          <InputLabel id="obj-ds-label">Data Source Override</InputLabel>
          <Select
            labelId="obj-ds-label"
            value={elementDS}
            label="Data Source Override"
            onChange={(e) => setElementDS(e.target.value)}
          >
            <MenuItem value=""><em>Inherit from slide</em></MenuItem>
            {dbs && dbs.map((item) => (
              <MenuItem key={item.name} value={item.name}>{item.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Query Override
          </Typography>
          <ToggleButtonGroup
            value={queryMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setQueryMode(v)}
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontSize: '0.75rem', textTransform: 'none' } }}
          >
            <ToggleButton value="prompt">Natural Language</ToggleButton>
            <ToggleButton value="sql">SQL</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {queryMode === 'prompt' && (
          <TextField
            margin="dense"
            label="Prompt / Intent Override"
            placeholder="Leave empty to inherit from slide"
            fullWidth
            variant="outlined"
            multiline
            minRows={3}
            value={elementIntent}
            onChange={(e) => setElementIntent(e.target.value)}
            helperText="Natural language description — LLM generates the SQL"
          />
        )}

        {queryMode === 'sql' && (
          <>
            <TextField
              margin="dense"
              label="SQL Query Override"
              placeholder={"SELECT * FROM sales\nWHERE month = '{{RelativeMonth|-1}}'\n  AND year = '{{RelativeYear|0|%Y}}'"}
              fullWidth
              variant="outlined"
              multiline
              minRows={5}
              value={elementQuery}
              onChange={(e) => setElementQuery(e.target.value)}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
              helperText="Use {{pattern}} for dynamic dates — see reference below"
            />
            <PatternReference />
          </>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
}
